import { CartActions } from '@theme/cart-api';

const OOS_RESERVE_ENABLED = true;

const PRODUCT_DETAILS_CART_CONFIG = {
  buttonSelector: '.product-details-cart-button',
  counterSelector: '[data-product-details-counter]',
  goToCartSelector: '[data-product-details-go-to-cart]',
  plusSelector: '[data-product-details-plus]',
  minusSelector: '[data-product-details-minus]',
  countSelector: '[data-product-details-count]',
  textSelector: '.product-details-cart-button__text'
};

const MOBILE_BAR_CART_CONFIG = {
  buttonSelector: '.mobile-product-bar__add-button',
  counterSelector: '[data-mobile-bar-counter]',
  goToCartSelector: '[data-mobile-bar-go-to-cart]',
  plusSelector: '[data-mobile-bar-plus]',
  minusSelector: '[data-mobile-bar-minus]',
  countSelector: '[data-mobile-bar-count]',
  textSelector: '.mobile-product-bar__add-text'
};

function getThemeTranslation(key, fallback) {
  const translations = typeof Theme !== 'undefined' && Theme.translations ? Theme.translations : {};
  return translations[key] || fallback;
}

function getCartButtonDefaultLabel(button, showPreOrder) {
  if (showPreOrder) {
    return button.dataset.reserveLabel || getThemeTranslation('reserve_now', 'Reserve now');
  }
  return button.dataset.defaultLabel || getThemeTranslation('add_to_cart', 'Add to cart');
}

function isReserveEligibleVariant(variant) {
  if (!variant) return false;
  if (variant.inventory_management !== 'shopify') return false;
  const quantity = Number(variant.inventory_quantity || 0);
  return quantity <= 0 && variant.inventory_policy === 'deny';
}

function isVariantSoldOut(variant) {
  if (!variant) return true;
  if (isReserveEligibleVariant(variant)) return false;
  if (variant.available === false) return true;
  if (variant.inventory_management === 'shopify') {
    const quantity = Number(variant.inventory_quantity || 0);
    if (quantity <= 0 && variant.inventory_policy === 'deny') {
      return true;
    }
  }
  return false;
}

function applyCartButtonAvailability(button, textElement, variant) {
  const reserveEligible = isReserveEligibleVariant(variant);
  const soldOut = isVariantSoldOut(variant);

  delete button.dataset.soldOut;

  if (reserveEligible) {
    button.disabled = false;
    button.removeAttribute('aria-disabled');
    button.setAttribute('data-reserve-product', '');
    button.classList.add('mobile-product-bar__add-button--reserve');
    if (textElement) {
      textElement.textContent = getCartButtonDefaultLabel(button, true);
    }
    return;
  }

  button.removeAttribute('data-reserve-product');
  button.classList.remove('mobile-product-bar__add-button--reserve');

  if (soldOut) {
    button.disabled = true;
    button.dataset.soldOut = 'true';
    button.setAttribute('aria-disabled', 'true');
    if (textElement) {
      textElement.textContent = getThemeTranslation('sold_out', 'Sold out');
    }
    return;
  }

  button.disabled = false;
  button.removeAttribute('aria-disabled');
  if (textElement) {
    textElement.textContent = getCartButtonDefaultLabel(button, false);
  }
}

function resolveCartButtonSetup(container) {
  if (container.classList.contains('product-details-cart-button-wrapper')) {
    return { wrapper: container, config: PRODUCT_DETAILS_CART_CONFIG };
  }
  if (container.classList.contains('mobile-product-bar__add-button-wrapper')) {
    return { wrapper: container, config: MOBILE_BAR_CART_CONFIG };
  }
  if (container.classList.contains('mobile-product-bar') || container.querySelector('.mobile-product-bar__add-button-wrapper')) {
    return {
      wrapper: null,
      config: {
        wrapperSelector: '.mobile-product-bar__add-button-wrapper',
        ...MOBILE_BAR_CART_CONFIG
      }
    };
  }
  return {
    wrapper: null,
    config: {
      wrapperSelector: '.product-details-cart-button-wrapper',
      ...PRODUCT_DETAILS_CART_CONFIG
    }
  };
}

class CartButtonManager {
  constructor(container, config = {}) {
    this.container = container;
    
    if (!this.container) return;
    
    const setup = resolveCartButtonSetup(container);
    this.wrapper = setup.wrapper;
    this.config = setup.config;
    
    Object.keys(config).forEach(key => {
      if (config[key]) this.config[key] = config[key];
    });
    
    this.init();
  }

  init() {
    const bindRoot = this.wrapper || this.container;
    if (!bindRoot || bindRoot.dataset.cartManagerBound === 'true') return;
    bindRoot.dataset.cartManagerBound = 'true';

    this.setupAddToCartButton();
    this.syncWithCart();

    this._cartUpdatedHandler = (e) => {
      const cart = e.detail?.cart;
      this.syncWithCart(cart);
      const wrapper = this.wrapper || (this.config.wrapperSelector ? this.container.querySelector(this.config.wrapperSelector) : this.container);
      const variantId = wrapper?.dataset.variantId || this.container.querySelector(this.config.buttonSelector)?.dataset.variantId;
      if (cart && variantId) {
        this.updatePrice(cart, variantId);
      }
    };
    document.addEventListener('cart:updated', this._cartUpdatedHandler);
  }

  async syncWithCart(cart = null) {
    const wrapper = this.wrapper || (this.config.wrapperSelector ? this.container.querySelector(this.config.wrapperSelector) : this.container);
    const button = this.container.querySelector(this.config.buttonSelector);
    const counter = this.container.querySelector(this.config.counterSelector);
    const countElement = this.container.querySelector(this.config.countSelector);
    
    if (!button || !wrapper) return;

    const variantId = wrapper.dataset.variantId || button.dataset.variantId;
    if (!variantId) return;

    try {
      if (!cart) {
        cart = await fetch('/cart.js').then(r => r.json());
      }

      const cartItem = cart.items?.find(item => item.variant_id == variantId);
      const quantity = cartItem ? cartItem.quantity : 0;

      // Атомарное переключение - сначала скрываем обе, потом показываем нужную
      if (quantity > 0) {
        // Скрываем кнопку сразу
        button.style.display = 'none';
        button.style.visibility = 'hidden';
        // Показываем counter
        if (counter) {
          counter.style.display = 'flex';
          counter.style.visibility = 'visible';
        }
        if (countElement) {
          countElement.textContent = String(cart.item_count || quantity);
        }
      } else {
        // Скрываем counter сразу
        if (counter) {
          counter.style.display = 'none';
          counter.style.visibility = 'hidden';
        }
        // Показываем кнопку
        button.style.display = 'flex';
        button.style.visibility = 'visible';
        const showPreOrder = button.hasAttribute('data-reserve-product');
        const textElement = button.querySelector(this.config.textSelector);
        if (textElement) {
          textElement.textContent = getCartButtonDefaultLabel(button, showPreOrder);
        }
      }
    } catch (error) {
      console.error('Error syncing with cart:', error);
    }
  }

  setupAddToCartButton() {
    const wrapper = this.wrapper || (this.config.wrapperSelector ? this.container.querySelector(this.config.wrapperSelector) : this.container);
    const button = this.container.querySelector(this.config.buttonSelector);
    const goToCart = this.container.querySelector(this.config.goToCartSelector);
    const plus = this.container.querySelector(this.config.plusSelector);
    const minus = this.container.querySelector(this.config.minusSelector);
    
    if (!button || !wrapper) {
      console.warn('CartButtonManager: button or wrapper not found', { 
        button, 
        wrapper, 
        container: this.container,
        buttonSelector: this.config.buttonSelector,
        wrapperSelector: this.config.wrapperSelector
      });
      return;
    }

    if (goToCart) {
      goToCart.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        window.location.href = '/cart';
      });
      goToCart.style.cursor = 'pointer';
    }

      const handleAddClick = async (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Читаем variantId заново при каждом клике
      const currentVariantId = wrapper.dataset.variantId || button.dataset.variantId;
      if (!currentVariantId) return;
      
      if (OOS_RESERVE_ENABLED && button.hasAttribute('data-reserve-product')) {
        const productId = button.dataset.productId;
        const variantId = button.dataset.variantId;
        const title = button.dataset.productTitle;
        const image = button.dataset.productImage;
        
        if (window.openReservePopup) {
          window.openReservePopup({
            productId: productId,
            variantId: variantId,
            title: title,
            image: image,
            productPath: button.dataset.productPath,
            productUrl: button.dataset.productUrl,
            sku: button.dataset.variantSku
          });
        }
        return;
      }
      
      if (button.disabled) return;

      button.disabled = true;
      const showPreOrder = button.hasAttribute('data-reserve-product');
      const defaultText = getCartButtonDefaultLabel(button, showPreOrder);
      const textElement = button.querySelector(this.config.textSelector);
      if (textElement) {
        textElement.textContent = getThemeTranslation('adding_to_cart', 'Adding...');
      }

      try {
        const updatedCart = await CartActions.add(currentVariantId);
        if (!updatedCart) {
          button.disabled = false;
          if (textElement) textElement.textContent = defaultText;
          return;
        }

        button.disabled = false;
        this.syncWithCart(updatedCart);

      } catch (error) {
        console.error('Error adding to cart:', error);
        if (textElement) {
          textElement.textContent = getThemeTranslation('add_to_cart_error', 'Error adding to cart');
        }
        
        setTimeout(() => {
          if (textElement) {
            textElement.textContent = defaultText;
          }
          button.disabled = false;
        }, 2000);
      }
    };

    button.addEventListener('click', handleAddClick);

    if (plus) {
      plus.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Читаем variantId заново при каждом клике
        const currentVariantId = wrapper.dataset.variantId || button.dataset.variantId;
        if (!currentVariantId) return;
        
        if (plus.disabled) return;
        plus.disabled = true;
        if (minus) minus.disabled = true;

        try {
          const updatedCart = await CartActions.inc(currentVariantId);
          if (!updatedCart) return;

          this.syncWithCart(updatedCart);
          this.updatePrice(updatedCart, currentVariantId);
        } catch (error) {
          console.error('Error increasing quantity:', error);
        } finally {
          plus.disabled = false;
          if (minus) minus.disabled = false;
        }
      });
    }

    if (minus) {
      minus.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Читаем variantId заново при каждом клике
        const currentVariantId = wrapper.dataset.variantId || button.dataset.variantId;
        if (!currentVariantId) return;
        
        if (minus.disabled) return;
        minus.disabled = true;
        if (plus) plus.disabled = true;

        try {
          const updatedCart = await CartActions.dec(currentVariantId);
          if (!updatedCart) return;

          this.syncWithCart(updatedCart);
          this.updatePrice(updatedCart, currentVariantId);
        } catch (error) {
          console.error('Error decreasing quantity:', error);
        } finally {
          minus.disabled = false;
          if (plus) plus.disabled = false;
        }
      });
    }
  }

  async onVariantChange(variant) {
    if (!variant) return;

    const wrapper = this.wrapper || (this.config.wrapperSelector ? this.container.querySelector(this.config.wrapperSelector) : this.container);
    const button = this.container.querySelector(this.config.buttonSelector);
    const counter = this.container.querySelector(this.config.counterSelector);
    const textElement = button?.querySelector(this.config.textSelector);

    if (!wrapper || !button) return;

    wrapper.dataset.variantId = variant.id;
    button.dataset.variantId = variant.id;
    button.disabled = false;

    const productPath = button.dataset.productPath || '';
    if (productPath && variant.id) {
      const normalizedPath = productPath.startsWith('/') ? productPath : `/${productPath}`;
      const productUrl = `${window.location.origin}${normalizedPath}?variant=${variant.id}`;
      button.dataset.productUrl = productUrl;
      button.setAttribute('data-product-url', productUrl);
    }

    const sku = variant.sku && String(variant.sku).trim() !== '' ? String(variant.sku).trim() : '';
    button.dataset.variantSku = sku;
    button.setAttribute('data-variant-sku', sku);

    if (counter) {
      counter.style.display = 'none';
      counter.style.visibility = 'hidden';
    }
    button.style.display = 'flex';
    button.style.visibility = 'visible';

    applyCartButtonAvailability(button, textElement, variant);

    const outerWrapper = this.container.closest('.product-details-cart-button-wrapper')
      || (this.container.classList.contains('product-details-cart-button-wrapper') ? this.container : null);
    if (outerWrapper) {
      outerWrapper.dataset.variantId = variant.id;
      outerWrapper.dataset.estimateVariantId = variant.id;
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));
    await this.syncWithCart();
  }

  updatePrice(cart, variantId) {
    let didUpdate = false;

    const pdpWrapper = document.querySelector('.product-details-cart-button-wrapper[data-variant-id="' + variantId + '"]');

    document.querySelectorAll('.price-container[data-product-id]').forEach((priceContainer) => {
      const containerVariantId = priceContainer.dataset.variantId;
      const containerProductId = priceContainer.dataset.productId;
      if (containerVariantId != variantId) {
        if (!pdpWrapper || containerProductId != pdpWrapper.dataset.productId) return;
        priceContainer.setAttribute('data-variant-id', String(variantId));
      }

      const cartItem = cart.items?.find((item) => item.variant_id == variantId);
      const quantity = cartItem ? cartItem.quantity : 0;
      const displayQty = quantity > 0 ? quantity : 1;

      const unitPrice = parseInt(priceContainer.dataset.unitPrice, 10) || 0;
      if (unitPrice <= 0) return;

      const dataEl = priceContainer.querySelector('[data-price-minor]');
      if (!dataEl) return;

      let unitCompare = parseInt(dataEl.getAttribute('data-unit-compare-minor'), 10) || 0;
      if (unitCompare <= unitPrice) {
        unitCompare = 0;
      }

      dataEl.setAttribute('data-price-minor', String(unitPrice * displayQty));
      if (unitCompare > unitPrice) {
        dataEl.setAttribute('data-compare-minor', String(unitCompare * displayQty));
      } else {
        dataEl.removeAttribute('data-compare-minor');
      }
      didUpdate = true;
    });

    const mobileBar = document.querySelector('[data-mobile-bar]');
    if (mobileBar) {
      const variantIdAttr = mobileBar.querySelector('[data-variant-id]');
      if (variantIdAttr && variantIdAttr.dataset.variantId == variantId) {
        const mobilePriceWrap = mobileBar.querySelector('.mobile-product-bar__price');
        if (mobilePriceWrap) {
          const cartItem = cart.items?.find((item) => item.variant_id == variantId);
          const quantity = cartItem ? cartItem.quantity : 0;
          const displayQty = quantity > 0 ? quantity : 1;

          let unitPrice = 0;
          const priceContainer = document.querySelector(`.price-container[data-variant-id="${variantId}"]`);
          if (priceContainer) {
            unitPrice = parseInt(priceContainer.dataset.unitPrice, 10) || 0;
          }

          if (unitPrice > 0) {
            let unitCompareMb = parseInt(mobilePriceWrap.getAttribute('data-unit-compare-minor'), 10) || 0;
            if (unitCompareMb <= unitPrice) {
              unitCompareMb = 0;
            }
            mobilePriceWrap.setAttribute('data-price-minor', String(unitPrice * displayQty));
            if (unitCompareMb > unitPrice) {
              mobilePriceWrap.setAttribute('data-compare-minor', String(unitCompareMb * displayQty));
            } else {
              mobilePriceWrap.removeAttribute('data-compare-minor');
            }
            didUpdate = true;
          }
        }
      }
    }

    if (didUpdate) {
      document.dispatchEvent(new CustomEvent('theme:convert-prices'));
    }
  }
}

class MobileProductBar {
  constructor() {
    this.bar = document.querySelector('[data-mobile-bar]');
    if (!this.bar) return;

    if (this.bar.dataset.mobileBarBound === 'true') {
      window.mobileProductBarInstance = window.mobileProductBarInstance || this;
      return;
    }
    this.bar.dataset.mobileBarBound = 'true';

    window.mobileProductBarInstance = this;
    this.init();
  }

  init() {
    const isMobile = window.innerWidth <= 1040;

    if (isMobile) {
      requestAnimationFrame(() => {
        this.bar.classList.add('visible');
      });
    }

    this.cartButtonManager = new CartButtonManager(this.bar);

    this._variantUpdateHandler = (e) => {
      this.updateButtonForVariant(e.detail?.resource);
    };
    document.addEventListener('variant:update', this._variantUpdateHandler);

    this._resizeHandler = () => {
      const isMobileNow = window.innerWidth <= 1040;
      if (isMobileNow && !this.bar.classList.contains('visible')) {
        this.bar.classList.add('visible');
      } else if (!isMobileNow && this.bar.classList.contains('visible')) {
        this.bar.classList.remove('visible');
      }
    };
    window.addEventListener('resize', this._resizeHandler);
  }

  async updateButtonForVariant(variant) {
    if (!variant || !this.cartButtonManager) return;
    await this.cartButtonManager.onVariantChange(variant);
  }
}

function initProductDetailsButtons() {
  const productDetailsButtons = document.querySelectorAll('.product-details-cart-button-wrapper');
  productDetailsButtons.forEach(wrapper => {
    if (wrapper.closest('[data-mobile-bar]')) return;

    if (!wrapper.dataset.initialized) {
      const buttonWrapper = wrapper.querySelector('.mobile-product-bar__add-button-wrapper');
      if (buttonWrapper) {
        const cartButtonManager = new CartButtonManager(buttonWrapper);
        buttonWrapper._cartButtonManager = cartButtonManager;
        wrapper.dataset.initialized = 'true';
      }
    }
  });
}

function bindPdpCartVariantSync() {
  if (document.documentElement.dataset.pdpCartVariantSyncBound === 'true') return;
  document.documentElement.dataset.pdpCartVariantSyncBound = 'true';

  document.addEventListener('variant:update', (e) => {
    const variant = e.detail?.resource;
    if (!variant) return;

    document.querySelectorAll('.product-details-cart-button-wrapper').forEach((outerWrapper) => {
      if (outerWrapper.closest('[data-mobile-bar]')) return;
      const buttonWrapper = outerWrapper.querySelector('.mobile-product-bar__add-button-wrapper');
      const manager = buttonWrapper?._cartButtonManager;
      if (manager) {
        manager.onVariantChange(variant);
      }
    });
  });
}

function initCartButtons() {
  if (!window.mobileProductBarInstance) {
    const instance = new MobileProductBar();
    if (instance && instance.bar) {
      window.mobileProductBarInstance = instance;
    }
  }
  initProductDetailsButtons();
  bindPdpCartVariantSync();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(initCartButtons, 100);
  });
} else {
  setTimeout(initCartButtons, 100);
}

document.addEventListener('shopify:section:load', () => {
  setTimeout(initCartButtons, 100);
  if (typeof window.__themeBindCardGalleryHover === 'function') {
    window.__themeBindCardGalleryHover();
  }
});

(function initCardGalleryFollowHover() {
  if (window.__cardGalleryFollowHoverInit) return;
  window.__cardGalleryFollowHoverInit = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const fineHover = window.matchMedia('(hover: hover) and (pointer: fine)');
  let pointerMoveRaf = 0;
  let pendingMove = null;
  let bindTimer = null;

  function getGallerySlides(slideshow) {
    return Array.from(slideshow.querySelectorAll('slideshow-slide')).filter((slide) => {
      return !slide.hasAttribute('hidden') || slide.hasAttribute('reveal');
    });
  }

  function getFadeLayer(gallery) {
    let layer = gallery.querySelector('.card-gallery__hover-fallback');
    if (layer instanceof HTMLImageElement) return layer;
    layer = document.createElement('img');
    layer.className = 'card-gallery__hover-fallback';
    layer.alt = '';
    layer.setAttribute('aria-hidden', 'true');
    layer.width = 600;
    layer.height = 750;
    return layer;
  }

  function visibleSlideImage(slideshow) {
    const slides = getGallerySlides(slideshow);
    const current = typeof slideshow.current === 'number' ? slideshow.current : 0;
    const slide = slides[current] || slides[0];
    const img = slide?.querySelector('img');
    return img instanceof HTMLImageElement ? img : null;
  }

  function selectSlideForGallery(gallery, clientX, options = {}) {
    const slideshow = gallery.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement)) return;
    if (typeof slideshow.select !== 'function') return;

    const slides = getGallerySlides(slideshow);
    if (slides.length <= 1) return;

    const rect = gallery.getBoundingClientRect();
    if (rect.width <= 0) return;

    let ratio = (clientX - rect.left) / rect.width;
    if (document.documentElement.getAttribute('dir') === 'rtl' || document.body?.dataset?.rtl === 'true') {
      ratio = 1 - ratio;
    }

    const normalized = Math.min(Math.max(ratio, 0), 0.999);
    const index = Math.min(slides.length - 1, Math.floor(normalized * slides.length));
    const targetIndex = options.forceIndex === undefined ? index : options.forceIndex;

    if (gallery.dataset.cardGalleryHoverIndex === String(targetIndex)) return;

    const skipFade = reduceMotion.matches;
    const layer = skipFade ? null : getFadeLayer(gallery);
    const overlayOn = layer ? parseFloat(window.getComputedStyle(layer).opacity) > 0.04 : false;

    if (layer && !overlayOn) {
      const currentImg = visibleSlideImage(slideshow);
      const src = currentImg ? currentImg.currentSrc || currentImg.src : '';
      if (src) {
        if (layer.currentSrc !== src && layer.src !== src) layer.src = src;
        if (!layer.parentElement) gallery.appendChild(layer);
        layer.style.transition = 'none';
        layer.style.opacity = '1';
        void layer.offsetWidth;
      }
    }

    gallery.dataset.cardGalleryHoverIndex = String(targetIndex);
    slideshow.select(targetIndex, undefined, { animate: false });

    if (!layer) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        layer.style.removeProperty('transition');
        layer.style.opacity = '0';
      });
    });
  }

  function resetGallery(gallery) {
    gallery.removeAttribute('data-card-gallery-hover-active');

    const slideshow = gallery.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement)) return;
    if (typeof slideshow.select !== 'function') return;
    if (getGallerySlides(slideshow).length <= 1) return;

    const current = gallery.dataset.cardGalleryHoverIndex;
    if (!current || current === '0') {
      gallery.dataset.cardGalleryHoverIndex = '';
      slideshow.select(0, undefined, { animate: false });
      return;
    }

    gallery.dataset.cardGalleryHoverIndex = '';
    selectSlideForGallery(gallery, 0, { forceIndex: 0 });
  }

  function bindGallery(gallery) {
    if (!(gallery instanceof HTMLElement)) return;
    if (gallery.dataset.cardGalleryHoverBound === 'true') return;
    if (gallery.closest('product-card[data-disable-hover-preview="true"]')) return;

    gallery.dataset.cardGalleryHoverBound = 'true';

    gallery.addEventListener('pointerenter', (event) => {
      if (!fineHover.matches || reduceMotion.matches) return;
      if (!(event instanceof PointerEvent)) return;
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      gallery.dataset.cardGalleryHoverActive = 'true';
      selectSlideForGallery(gallery, event.clientX);
    });

    gallery.addEventListener('pointermove', (event) => {
      if (!fineHover.matches || reduceMotion.matches) return;
      if (!(event instanceof PointerEvent)) return;
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      pendingMove = { gallery, clientX: event.clientX };
      if (pointerMoveRaf) return;
      pointerMoveRaf = requestAnimationFrame(() => {
        pointerMoveRaf = 0;
        const next = pendingMove;
        pendingMove = null;
        if (!next) return;
        selectSlideForGallery(next.gallery, next.clientX);
      });
    });

    gallery.addEventListener('pointerleave', (event) => {
      if (!(event instanceof PointerEvent)) return;
      if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
      resetGallery(gallery);
    });
  }

  function bindAll(root = document) {
    const scope = root instanceof Element || root instanceof Document ? root : document;
    scope.querySelectorAll('[data-card-gallery-follow-hover]').forEach(bindGallery);
  }

  window.__themeBindCardGalleryHover = bindAll;

  const resetAllGalleriesWhenHoverIsLost = (event) => {
    if (event.matches) return;
    document.querySelectorAll('[data-card-gallery-follow-hover]').forEach(resetGallery);
  };

  if (typeof fineHover.addEventListener === 'function') {
    fineHover.addEventListener('change', resetAllGalleriesWhenHoverIsLost);
  } else if (typeof fineHover.addListener === 'function') {
    fineHover.addListener(resetAllGalleriesWhenHoverIsLost);
  }

  const start = () => {
    customElements.whenDefined('slideshow-component').then(() => {
      bindAll();
    }).catch(() => {
      bindAll();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof Element)) return;
    bindAll(event.target);
  });

  const observer = new MutationObserver((mutations) => {
    let shouldBindCart = false;
    let shouldBindHover = false;
    let hoverRoot = null;

    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (
          node.matches?.('.product-details-cart-button-wrapper, .mobile-product-bar__add-button-wrapper') ||
          node.querySelector?.('.product-details-cart-button-wrapper, .mobile-product-bar__add-button-wrapper')
        ) {
          shouldBindCart = true;
        }

        if (
          node.matches?.('[data-card-gallery-follow-hover]') ||
          node.querySelector?.('[data-card-gallery-follow-hover]')
        ) {
          shouldBindHover = true;
          hoverRoot = node;
        }
      }
    }

    if (!shouldBindCart && !shouldBindHover) return;

    if (bindTimer) clearTimeout(bindTimer);
    bindTimer = setTimeout(() => {
      bindTimer = null;
      if (shouldBindCart) initProductDetailsButtons();
      if (shouldBindHover) bindAll(hoverRoot || document);
    }, 80);
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    }, { once: true });
  }
})();
